<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  automationId: {
    type: [Number, String],
    default: null,
  },
  publishedVersionId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['published']);

const { t } = useI18n();
const store = useStore();

const versions = ref([]);
const governance = ref(null);
const publicationReason = ref('');
const isLoadingVersions = ref(false);
const isPublishing = ref(false);
const feedbackState = ref('idle');
const feedbackDetail = ref('');
const preflightIssues = ref([]);
let activeRequestId = 0;

const newestFirst = records =>
  [...records].sort((first, second) => {
    const firstNumber = Number(first.version_number || first.id || 0);
    const secondNumber = Number(second.version_number || second.id || 0);
    return secondNumber - firstNumber;
  });

function formatStatus(value) {
  if (value === null || value === undefined || value === '') {
    return t('AUTOMATION.PUBLICATION.UNAVAILABLE');
  }
  return value.toString().replaceAll('_', ' ');
}

const approvedCandidate = computed(() =>
  newestFirst(versions.value).find(version => version.status === 'approved')
);

const publishedVersion = computed(() => {
  if (props.publishedVersionId) {
    return versions.value.find(
      version => String(version.id) === String(props.publishedVersionId)
    );
  }

  return newestFirst(versions.value).find(
    version => version.status === 'published'
  );
});

const publicationGate = computed(() => governance.value?.publication || {});
const approvalState = computed(() => governance.value?.approval || {});
const riskSummary = computed(() => governance.value?.risk || {});
const diffSummary = computed(() => governance.value?.diff?.summary || {});
const impactEstimate = computed(() => governance.value?.impact_estimate || {});
const executionSummary = computed(() => governance.value?.execution || {});
const riskActions = computed(() => riskSummary.value.actions || []);
const publicationBlockers = computed(
  () => publicationGate.value.blockers || []
);
const hasPublicationBlockers = computed(
  () => publicationBlockers.value.length > 0
);
const publicationReasonValid = computed(
  () => publicationReason.value.trim().length >= 8
);

const canPublish = computed(
  () =>
    Boolean(approvedCandidate.value) &&
    !isLoadingVersions.value &&
    !hasPublicationBlockers.value &&
    publicationReasonValid.value
);

const governanceCards = computed(() => [
  {
    key: 'approval',
    label: t('AUTOMATION.PUBLICATION.GOVERNANCE.APPROVAL'),
    value: formatStatus(approvalState.value.status),
  },
  {
    key: 'diff',
    label: t('AUTOMATION.PUBLICATION.GOVERNANCE.DIFF'),
    value: t('AUTOMATION.PUBLICATION.GOVERNANCE.DIFF_VALUE', {
      count: diffSummary.value.total_changes ?? 0,
    }),
  },
  {
    key: 'impact',
    label: t('AUTOMATION.PUBLICATION.GOVERNANCE.IMPACT'),
    value: t('AUTOMATION.PUBLICATION.GOVERNANCE.IMPACT_VALUE', {
      fanout: impactEstimate.value.estimated_matching_conversations ?? 0,
      executions: impactEstimate.value.estimated_action_executions ?? 0,
    }),
  },
  {
    key: 'risk',
    label: t('AUTOMATION.PUBLICATION.GOVERNANCE.RISK'),
    value: t('AUTOMATION.PUBLICATION.GOVERNANCE.RISK_VALUE', {
      level: formatStatus(riskSummary.value.highest_risk_level || 'none'),
      count: riskSummary.value.blocking_action_count ?? 0,
    }),
  },
]);

const feedbackMessage = computed(() => {
  if (isLoadingVersions.value) return t('AUTOMATION.PUBLICATION.LOADING');
  if (feedbackState.value === 'load_error') {
    return t('AUTOMATION.PUBLICATION.LOAD_ERROR');
  }
  if (feedbackState.value === 'conflict') {
    return t('AUTOMATION.PUBLICATION.CONFLICT');
  }
  if (feedbackState.value === 'permission') {
    return t('AUTOMATION.PUBLICATION.PERMISSION');
  }
  if (feedbackState.value === 'validation') {
    return t('AUTOMATION.PUBLICATION.VALIDATION');
  }
  if (feedbackState.value === 'error') {
    return t('AUTOMATION.PUBLICATION.ERROR');
  }
  if (feedbackState.value === 'success') {
    return t('AUTOMATION.PUBLICATION.SUCCESS');
  }
  if (!approvedCandidate.value) return t('AUTOMATION.PUBLICATION.EMPTY');
  if (hasPublicationBlockers.value) return t('AUTOMATION.PUBLICATION.BLOCKED');
  if (!publicationReasonValid.value) {
    return t('AUTOMATION.PUBLICATION.REASON_REQUIRED');
  }
  return t('AUTOMATION.PUBLICATION.READY');
});

const feedbackToneClass = computed(() => {
  if (['conflict', 'validation'].includes(feedbackState.value)) {
    return 'bg-n-amber-3 text-n-amber-12';
  }
  if (['load_error', 'error', 'permission'].includes(feedbackState.value)) {
    return 'bg-n-ruby-2 text-n-ruby-11';
  }
  if (feedbackState.value === 'success') {
    return 'bg-n-teal-3 text-n-teal-12';
  }
  return 'bg-n-slate-2 text-n-slate-11';
});

const feedbackRole = computed(() =>
  ['conflict', 'validation', 'load_error', 'error', 'permission'].includes(
    feedbackState.value
  )
    ? 'alert'
    : 'status'
);

function statusLabel(status) {
  switch (status) {
    case 'draft':
      return t('AUTOMATION.PUBLICATION.STATUS.DRAFT');
    case 'in_review':
      return t('AUTOMATION.PUBLICATION.STATUS.IN_REVIEW');
    case 'approved':
      return t('AUTOMATION.PUBLICATION.STATUS.APPROVED');
    case 'scheduled':
      return t('AUTOMATION.PUBLICATION.STATUS.SCHEDULED');
    case 'published':
      return t('AUTOMATION.PUBLICATION.STATUS.PUBLISHED');
    case 'paused':
      return t('AUTOMATION.PUBLICATION.STATUS.PAUSED');
    case 'superseded':
      return t('AUTOMATION.PUBLICATION.STATUS.SUPERSEDED');
    case 'archived':
      return t('AUTOMATION.PUBLICATION.STATUS.ARCHIVED');
    default:
      return t('AUTOMATION.PUBLICATION.STATUS.UNKNOWN');
  }
}

function versionLabel(version) {
  if (!version) return t('AUTOMATION.PUBLICATION.VERSION_EMPTY');
  if (version.version_number) {
    return t('AUTOMATION.PUBLICATION.VERSION_LABEL', {
      version: version.version_number,
    });
  }
  return t('AUTOMATION.PUBLICATION.VERSION_ID', { id: version.id });
}

function versionLockLabel(version) {
  if (
    !version ||
    version.lock_version === undefined ||
    version.lock_version === null
  ) {
    return '';
  }
  return t('AUTOMATION.PUBLICATION.LOCK_VERSION', {
    version: version.lock_version,
  });
}

function issueLabel(issue) {
  const field = issue.path || issue.field || issue.section || '-';
  const message = issue.message || issue.code;
  return t('AUTOMATION.PUBLICATION.ISSUE', { field, message });
}

function blockerLabel(blocker) {
  const labels = {
    candidate_must_be_approved: t(
      'AUTOMATION.PUBLICATION.BLOCKERS.CANDIDATE_MUST_BE_APPROVED'
    ),
    candidate_base_is_not_published_version: t(
      'AUTOMATION.PUBLICATION.BLOCKERS.CANDIDATE_BASE_IS_NOT_PUBLISHED_VERSION'
    ),
  };
  return labels[blocker] || blocker;
}

function mapErrorState(error) {
  const status = error?.response?.status;
  const errorMessage = error?.response?.data?.error || '';
  preflightIssues.value = error?.response?.data?.issues || [];
  feedbackDetail.value = errorMessage;

  if (status === 409) return 'conflict';
  if (status === 403 || /authori[sz]ed|permission/i.test(errorMessage)) {
    return 'permission';
  }
  if (status === 422 || preflightIssues.value.length) return 'validation';
  return 'error';
}

async function loadVersions({ preserveFeedback = false } = {}) {
  const automationId = props.automationId;
  const requestId = activeRequestId + 1;
  activeRequestId = requestId;
  versions.value = [];
  governance.value = null;
  feedbackDetail.value = '';
  preflightIssues.value = [];

  if (!automationId) return;

  isLoadingVersions.value = true;
  if (!preserveFeedback) feedbackState.value = 'idle';

  try {
    const payload = await store.dispatch('automations/versions', automationId);
    if (requestId === activeRequestId) {
      versions.value = payload?.versions || [];
      governance.value = payload?.governance || null;
    }
  } catch (error) {
    if (requestId === activeRequestId) {
      feedbackState.value =
        error?.response?.status === 403 ? 'permission' : 'load_error';
      feedbackDetail.value = error?.response?.data?.error || '';
    }
  } finally {
    if (requestId === activeRequestId) {
      isLoadingVersions.value = false;
    }
  }
}

async function publishApprovedVersion() {
  const candidate = approvedCandidate.value;
  if (!candidate || isPublishing.value) return;

  isPublishing.value = true;
  feedbackState.value = 'idle';
  feedbackDetail.value = '';
  preflightIssues.value = [];

  try {
    const payload = await store.dispatch('automations/publishVersion', {
      id: props.automationId,
      versionId: candidate.id,
      lockVersion: candidate.lock_version,
      publicationMetadata: {
        source: 'editor',
        reason: publicationReason.value.trim(),
      },
    });
    feedbackState.value = 'success';
    emit('published', payload);
    await loadVersions({ preserveFeedback: true });
  } catch (error) {
    feedbackState.value = mapErrorState(error);
  } finally {
    isPublishing.value = false;
  }
}

watch(() => [props.automationId, props.publishedVersionId], loadVersions, {
  immediate: true,
});
</script>

<template>
  <section
    class="mb-5 rounded-lg border border-n-weak bg-n-background p-3"
    data-test="automation-publication-panel"
  >
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="min-w-0">
        <h3 class="mb-1 text-sm font-medium text-n-slate-12">
          {{ $t('AUTOMATION.PUBLICATION.TITLE') }}
        </h3>
        <p class="m-0 text-xs text-n-slate-11">
          {{ $t('AUTOMATION.PUBLICATION.SUBTITLE') }}
        </p>
      </div>
      <Button
        faded
        slate
        sm
        icon="i-lucide-refresh-cw"
        :label="$t('AUTOMATION.PUBLICATION.REFRESH')"
        :is-loading="isLoadingVersions"
        data-test="automation-publication-refresh"
        @click="loadVersions"
      />
    </div>

    <div class="mt-3 grid gap-2 sm:grid-cols-2">
      <div class="rounded-lg border border-n-weak px-3 py-2">
        <div class="text-xs font-medium text-n-slate-11">
          {{ $t('AUTOMATION.PUBLICATION.CURRENT') }}
        </div>
        <div
          class="mt-1 text-sm font-medium text-n-slate-12"
          data-test="automation-publication-current"
        >
          {{ versionLabel(publishedVersion) }}
        </div>
        <div class="mt-1 text-xs text-n-slate-11">
          {{
            publishedVersion
              ? statusLabel(publishedVersion.status)
              : $t('AUTOMATION.PUBLICATION.UNPUBLISHED')
          }}
        </div>
      </div>

      <div class="rounded-lg border border-n-weak px-3 py-2">
        <div class="text-xs font-medium text-n-slate-11">
          {{ $t('AUTOMATION.PUBLICATION.CANDIDATE') }}
        </div>
        <div
          class="mt-1 text-sm font-medium text-n-slate-12"
          data-test="automation-publication-candidate"
        >
          {{ versionLabel(approvedCandidate) }}
        </div>
        <div class="mt-1 text-xs text-n-slate-11">
          {{
            approvedCandidate
              ? `${statusLabel(approvedCandidate.status)} ${versionLockLabel(
                  approvedCandidate
                )}`
              : $t('AUTOMATION.PUBLICATION.NO_CANDIDATE')
          }}
        </div>
      </div>
    </div>

    <div
      v-if="governance"
      class="mt-3 grid gap-3"
      data-test="automation-publication-governance"
    >
      <div class="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <div
          v-for="item in governanceCards"
          :key="item.key"
          class="rounded-lg border border-n-weak px-3 py-2"
          :data-test="`automation-publication-governance-${item.key}`"
        >
          <div class="text-xs font-medium text-n-slate-11">
            {{ item.label }}
          </div>
          <div class="mt-1 text-sm font-semibold text-n-slate-12">
            {{ item.value }}
          </div>
        </div>
      </div>

      <ul
        v-if="publicationBlockers.length"
        class="m-0 grid list-none gap-1 rounded-md bg-n-amber-3 px-3 py-2 text-xs text-n-amber-12"
        data-test="automation-publication-blockers"
      >
        <li v-for="blocker in publicationBlockers" :key="blocker">
          {{ blockerLabel(blocker) }}
        </li>
      </ul>

      <div
        class="rounded-lg border border-n-weak px-3 py-2"
        data-test="automation-publication-execution"
      >
        <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('AUTOMATION.PUBLICATION.GOVERNANCE.EXECUTION') }}
          </span>
          <span
            class="rounded-md px-2 py-1 text-xs font-medium"
            :class="
              executionSummary.blocks_on_risk
                ? 'bg-n-amber-3 text-n-amber-12'
                : 'bg-n-teal-3 text-n-teal-12'
            "
          >
            {{
              executionSummary.blocks_on_risk
                ? $t('AUTOMATION.PUBLICATION.GOVERNANCE.EXECUTION_BLOCKED')
                : $t('AUTOMATION.PUBLICATION.GOVERNANCE.EXECUTION_READY')
            }}
          </span>
        </div>
        <dl class="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-3 gap-y-1">
          <dt class="text-xs text-n-slate-11">
            {{ $t('AUTOMATION.PUBLICATION.GOVERNANCE.SERVICE') }}
          </dt>
          <dd class="min-w-0 break-all text-xs text-n-slate-12">
            {{ executionSummary.service || '-' }}
          </dd>
          <dt class="text-xs text-n-slate-11">
            {{ $t('AUTOMATION.PUBLICATION.GOVERNANCE.APPROVAL_GATE') }}
          </dt>
          <dd class="min-w-0 break-all text-xs text-n-slate-12">
            {{ executionSummary.approval_gate || '-' }}
          </dd>
        </dl>
      </div>

      <div
        v-if="riskActions.length"
        class="grid gap-2"
        data-test="automation-publication-risk-actions"
      >
        <div
          v-for="action in riskActions"
          :key="`${action.index}:${action.action_name}`"
          class="grid gap-1 rounded-md border border-n-weak px-3 py-2 text-xs text-n-slate-11 sm:grid-cols-[minmax(0,1fr)_7rem_7rem_8rem]"
        >
          <span class="truncate font-medium text-n-slate-12">
            {{ action.action_name }}
          </span>
          <span>{{ formatStatus(action.risk_level || 'none') }}</span>
          <span>
            {{
              action.requires_approval
                ? $t('AUTOMATION.PUBLICATION.GOVERNANCE.APPROVAL_REQUIRED')
                : $t('AUTOMATION.PUBLICATION.GOVERNANCE.APPROVAL_NOT_REQUIRED')
            }}
          </span>
          <span>
            {{
              action.blocks_execution
                ? $t('AUTOMATION.PUBLICATION.GOVERNANCE.BLOCKS_EXECUTION')
                : $t('AUTOMATION.PUBLICATION.GOVERNANCE.DOES_NOT_BLOCK')
            }}
          </span>
        </div>
      </div>
    </div>

    <div
      class="mt-3 rounded-md px-3 py-2 text-sm"
      :class="feedbackToneClass"
      :role="feedbackRole"
      data-test="automation-publication-feedback"
    >
      {{ feedbackMessage }}
      <span v-if="feedbackDetail" class="block pt-1 text-xs">
        {{ feedbackDetail }}
      </span>
    </div>

    <ul
      v-if="preflightIssues.length"
      class="m-0 mt-2 grid list-none gap-1 p-0 text-xs text-n-amber-11"
      data-test="automation-publication-issues"
    >
      <li
        v-for="issue in preflightIssues"
        :key="`${issue.code}-${issue.field}`"
      >
        {{ issueLabel(issue) }}
      </li>
    </ul>

    <label class="m-0 mt-3 grid gap-1 text-xs font-medium text-n-slate-11">
      {{ $t('AUTOMATION.PUBLICATION.REASON_LABEL') }}
      <textarea
        v-model="publicationReason"
        rows="3"
        class="m-0 min-h-20 resize-y rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm font-normal text-n-slate-12"
        :placeholder="$t('AUTOMATION.PUBLICATION.REASON_PLACEHOLDER')"
        data-test="automation-publication-reason"
      />
    </label>

    <div class="mt-3 flex justify-end">
      <Button
        solid
        blue
        sm
        icon="i-lucide-upload-cloud"
        :label="
          isPublishing
            ? $t('AUTOMATION.PUBLICATION.PUBLISHING')
            : $t('AUTOMATION.PUBLICATION.PUBLISH')
        "
        :disabled="!canPublish || isPublishing"
        :is-loading="isPublishing"
        data-test="automation-publication-publish"
        @click="publishApprovedVersion"
      />
    </div>
  </section>
</template>
